/**
 * ResetPasswordPage - Define a nova senha apos clicar no link de recuperacao
 *
 * Acessada via link enviado por email pelo Supabase.
 * Quando o usuario clica no link de recovery, o Supabase redireciona para:
 *   /reset-password#access_token=xxx&refresh_token=xxx&type=recovery
 *
 * O AuthContext detecta os tokens no hash da URL durante a inicializacao,
 * aplica a sessao e limpa o hash. Quando authLoading se torna false, a
 * sessao ja esta pronta (ou nao, indicando link invalido/expirado).
 *
 * O usuario informa a nova senha e confirma (PasswordChangeForm).
 * Apos sucesso, redireciona para o dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import PasswordChangeForm from '@/components/auth/PasswordChangeForm';
import { Loader2, KeyRound, CheckCircle2 } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading, mustChangePassword } = useAuth();

  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Ensure dark mode — matches LoginPage. Without this, the page inherits
  // the mode of whichever page was visited before (e.g. SignupPage forces
  // light mode, making the card render white on top of our dark gradient).
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // Wait for AuthContext to finish processing.
  // The AuthContext handles hash token parsing (from Supabase recovery redirect)
  // during its initialization. When authLoading becomes false, the session is
  // already applied (or absent, meaning the link is invalid/expired).
  useEffect(() => {
    if (!authLoading) {
      setSessionReady(true);
    }
  }, [authLoading]);

  const handleSuccess = () => {
    setSuccess(true);
    // Redirect to dashboard after short delay
    setTimeout(() => navigate('/'), 2000);
  };

  // Show loading while auth state is being processed
  if (!sessionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('reset.checkingLink')}</p>
        </div>
      </div>
    );
  }

  // If no user session, the link may be invalid or expired
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <CoreStudioLogo width={200} className="text-foreground" />
          </div>

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl font-bold">{t('reset.invalidLink')}</CardTitle>
              <CardDescription>{t('reset.invalidLinkDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/login')} className="w-full h-11">
                {t('reset.backToLogin')}
              </Button>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            {t('common.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center">
            <CoreStudioLogo width={200} className="text-foreground" />
          </div>

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-3">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-2xl font-bold">{t('reset.successTitle')}</CardTitle>
              <CardDescription>{t('reset.success')}</CardDescription>
            </CardHeader>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            {t('common.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center scanlines p-4" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <CoreStudioLogo width={200} className="text-foreground" />
        </div>

        {/* Reset Card */}
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <KeyRound className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">{t('reset.title')}</CardTitle>
            <CardDescription>{t('reset.subtitleShort')}</CardDescription>
          </CardHeader>

          <CardContent>
            {/* Senha temporária pendente: a API exige a política forte do signup */}
            <PasswordChangeForm strongPolicy={mustChangePassword} onSuccess={handleSuccess} />
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
